import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { apiClient, workoutsApi } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipTrigger, TooltipContent } from './ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Search, Dumbbell, X, ChevronDown, Clock, TrendingUp,
  Zap, Target, ArrowUpDown, Info, SlidersHorizontal, Plus
} from 'lucide-react';
import StaticExerciseImage from './StaticExerciseImage';
import CreateCustomExercise from './CreateCustomExercise';
import ExerciseDetailModal from './ExerciseDetailModal';
import { BODY_PARTS, EQUIPMENT_LIST_SHORT as EQUIPMENT_LIST } from '../lib/constants';
import { translateMuscle, translateEquipment, translateBodyPart } from '../lib/muscleTranslations';
import { exerciseName } from '../lib/exerciseDisplay';
import { useLanguage } from '../contexts/LanguageContext';


// Filter options
const TARGET_MUSCLES = [
  'Abs','Adductors','Biceps','Calves','Cardiovascular System','Delts',
  'Forearms','Glutes','Hamstrings','Lats','Pectorals','Quads',
  'Serratus Anterior','Traps','Triceps','Upper Back'
];

// Determine exercise type from data
function getExerciseType(ex) {
  const secondary = ex.secondaryMuscles || ex.secondary_muscles || [];
  if (secondary.length >= 2) return 'Compound';
  if (secondary.length === 0) return 'Isolation';
  return 'Compound';
}

function getExerciseCategory(ex) {
  const equip = (ex.equipment || '').toLowerCase();
  if (equip === 'body weight') return 'Bodyweight';
  if (['barbell', 'dumbbell', 'ez barbell', 'kettlebell', 'trap bar'].includes(equip)) return 'Free Weight';
  if (['cable', 'leverage machine', 'smith machine', 'sled machine'].includes(equip)) return 'Machine';
  if (['band', 'resistance band'].includes(equip)) return 'Band';
  return 'Other';
}

// Tiny badge for equipment/type
function TagBadge({ children, variant = 'default' }) {
  const colors = {
    default: 'bg-muted/60 text-muted-foreground border-border',
    muscle: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
    equipment: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
    type: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
    recent: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${colors[variant]}`}>
      {children}
    </span>
  );
}

// Single exercise row - Hevy style
function ExerciseRow({ exercise, onSelect, onDetail, isRecent, useCount }) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const type = getExerciseType(exercise);
  const category = getExerciseCategory(exercise);
  const muscles = exercise.targetMuscles || exercise.primary_muscles || [];
  const secondaryMuscles = exercise.secondaryMuscles || exercise.secondary_muscles || [];

  return (
    <div
      className="group flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer hover:bg-white/[0.04] active:bg-white/[0.08] transition-all duration-150"
      onClick={() => onSelect(exercise)}
      data-testid={`exercise-pick-${exercise.id || exercise.name}`}
    >
      {/* Thumbnail - Static image (no animated GIF) */}
      <div
        className="w-12 h-12 rounded-xl overflow-hidden bg-muted border border-border flex-shrink-0 relative"
        onClick={(e) => { e.stopPropagation(); onDetail(exercise); }}
      >
        <StaticExerciseImage src={exercise.gifUrl || exercise.image_url} alt={exercise.name} size="sm" />
        {/* Info overlay on hover */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Info className="h-4 w-4 text-white/80" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="font-semibold text-[13px] text-foreground truncate leading-tight">{exerciseName(exercise, language)}</p>
          {isRecent && <Clock className="h-3 w-3 text-purple-400 flex-shrink-0" />}
        </div>
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          {muscles[0] && <TagBadge variant="muscle">{translateMuscle(muscles[0], language)}</TagBadge>}
          {exercise.equipment && <TagBadge variant="equipment">{translateEquipment(exercise.equipment, language)}</TagBadge>}
          <TagBadge variant="type">{type}</TagBadge>
        </div>
        {secondaryMuscles.length > 0 && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {t('exercisePicker.also')} {secondaryMuscles.slice(0, 3).map(m => translateMuscle(m, language)).join(', ')}
          </p>
        )}
      </div>

      {/* Usage indicator */}
      {useCount > 0 && (
        <div className="flex-shrink-0 text-right">
          <span className="text-xs text-muted-foreground">{useCount}x</span>
        </div>
      )}
    </div>
  );
}

// Section header
function SectionHeader({ icon: Icon, title, count, color = 'text-muted-foreground' }) {
  return (
    <div className="flex items-center gap-2 px-3 pt-3 pb-1.5">
      <Icon className={`h-3.5 w-3.5 ${color}`} />
      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</span>
      {count !== undefined && (
        <span className="text-xs text-muted-foreground/60">({count})</span>
      )}
    </div>
  );
}


export default function ExercisePicker({ open, onClose, onSelect, title, replacingExercise }) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { user } = useAuth();

  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [correctedQuery, setCorrectedQuery] = useState(null);
  const [muscleFilter, setMuscleFilter] = useState('');
  const [equipmentFilter, setEquipmentFilter] = useState('');
  const [bodyPartFilter, setBodyPartFilter] = useState('');
  const [sortBy, setSortBy] = useState('relevance'); // relevance | alpha | muscle
  const [showFilters, setShowFilters] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [recentExercises, setRecentExercises] = useState([]);
  const [frequentExercises, setFrequentExercises] = useState([]);
  const [similarExercises, setSimilarExercises] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [userExerciseNames, setUserExerciseNames] = useState(new Set());
  const [exerciseDetail, setExerciseDetail] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // Detect mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Load user recent/frequent on open
  useEffect(() => {
    if (!open) return;
    const loadUserStats = async () => {
      try {
        const res = await workoutsApi.getUserRecentExercises();
        setRecentExercises(res.data.recent || []);
        setFrequentExercises(res.data.frequent || []);
        setUserExerciseNames(new Set(res.data.all_names || []));
      } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to load exercise history'); }
    };
    loadUserStats();
    // Load initial exercises
    fetchExercises('', '', '', '');
    // Set default tab
    setActiveTab(replacingExercise ? 'similar' : 'recent');
    // Load similar exercises when exchanging
    if (replacingExercise) {
      const loadSimilar = async () => {
        try {
          const muscle = (replacingExercise.targetMuscles || [])[0] || '';
          const bodyPart = replacingExercise.muscle_group || '';
          let url = `/exercises?limit=20`;
          if (muscle) url += `&target_muscle=${encodeURIComponent(muscle.toLowerCase())}`;
          else if (bodyPart) url += `&muscle_group=${encodeURIComponent(bodyPart)}`;
          if (user?.id) url += `&user_id=${encodeURIComponent(user.id)}`;
          const res = await apiClient.get(url);
          // Exclude the exercise being replaced
          setSimilarExercises((res.data || []).filter(ex => ex.name !== replacingExercise.name));
        } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to load similar exercises'); }
      };
      loadSimilar();
    }
  }, [open]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setSearch('');
      setMuscleFilter('');
      setEquipmentFilter('');
      setBodyPartFilter('');
      setShowFilters(false);
      setExerciseDetail(null);
      setShowSortMenu(false);
      setShowCreate(false);
      setSuggestions([]);
      setCorrectedQuery(null);
      setSimilarExercises([]);
      setActiveTab('all');
    }
  }, [open]);

  const fetchExercises = useCallback(async (searchVal, muscle, equipment, bodyPart, { keepResults = false } = {}) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    if (!keepResults) setLoading(true);
    setSuggestions([]);
    setCorrectedQuery(null);
    try {
      let url = `/exercises?limit=100`;
      if (searchVal) url += `&search=${encodeURIComponent(searchVal)}`;
      if (muscle) url += `&target_muscle=${encodeURIComponent(muscle.toLowerCase())}`;
      if (equipment) url += `&equipment=${encodeURIComponent(equipment)}`;
      if (bodyPart) url += `&muscle_group=${encodeURIComponent(bodyPart)}`;
      if (user?.id) url += `&user_id=${encodeURIComponent(user.id)}`;
      const res = await apiClient.get(url, { signal: controller.signal });
      setResults(res.data);
      
      // If no results and user typed something, fetch suggestions
      if (res.data.length === 0 && searchVal && searchVal.trim().length >= 2) {
        try {
          const sugRes = await apiClient.get(`/exercises/suggest?search=${encodeURIComponent(searchVal)}`, { signal: controller.signal });
          setSuggestions(sugRes.data.suggestions || []);
          setCorrectedQuery(sugRes.data.corrected_query || null);
        } catch { }
      }
    } catch (err) {
      if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') setResults([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Debounced search
  const handleSearchChange = (val) => {
    setSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchExercises(val, muscleFilter, equipmentFilter, bodyPartFilter);
    }, 200);
  };

  const applyFilter = (type, value) => {
    const newMuscle = type === 'muscle' ? (muscleFilter === value ? '' : value) : muscleFilter;
    const newEquip = type === 'equipment' ? (equipmentFilter === value ? '' : value) : equipmentFilter;
    const newBody = type === 'bodyPart' ? (bodyPartFilter === value ? '' : value) : bodyPartFilter;
    setMuscleFilter(newMuscle);
    setEquipmentFilter(newEquip);
    setBodyPartFilter(newBody);
    setActiveTab('all');
    fetchExercises(search, newMuscle, newEquip, newBody, { keepResults: true });
  };

  const clearAllFilters = () => {
    setMuscleFilter('');
    setEquipmentFilter('');
    setBodyPartFilter('');
    fetchExercises(search, '', '', '', { keepResults: true });
  };

  const hasFilters = muscleFilter || equipmentFilter || bodyPartFilter;

  // Sort results
  const sortedResults = [...results].sort((a, b) => {
    // User's exercises always get a boost
    const aUser = userExerciseNames.has(a.name) ? 1 : 0;
    const bUser = userExerciseNames.has(b.name) ? 1 : 0;
    if (aUser !== bUser) return bUser - aUser;

    if (sortBy === 'alpha') return (a.name || '').localeCompare(b.name || '');
    if (sortBy === 'muscle') {
      const aM = (a.targetMuscles || [])[0] || '';
      const bM = (b.targetMuscles || [])[0] || '';
      return aM.localeCompare(bM) || (a.name || '').localeCompare(b.name || '');
    }
    return 0; // relevance = server order (popularity + search match)
  });

  // Show sections
  const showRecents = !search && !hasFilters && recentExercises.length > 0;
  const showFrequent = !search && !hasFilters && frequentExercises.length > 0;

  const handleSelect = (exercise) => {
    onSelect(exercise);
    onClose();
  };

  const handleDetail = (exercise) => {
    setExerciseDetail(exercise);
  };

  const activeFilterCount = [muscleFilter, equipmentFilter, bodyPartFilter].filter(Boolean).length;

  // ─── Content ───────────────────────────────────────────────────
  const pickerContent = (
    <div className="flex flex-col flex-1 min-h-0 relative" data-testid="exercise-picker">
      {showCreate ? (
        <CreateCustomExercise
          onBack={() => setShowCreate(false)}
          onCreated={(exercise) => {
            setShowCreate(false);
            handleSelect(exercise);
          }}
          prefillName={search}
        />
      ) : (
      <>
      {/* Replacing banner */}
      {replacingExercise && (
        <div className="flex items-center gap-2.5 mx-4 mt-1 mb-1 px-3 py-2 rounded-xl bg-muted/50 border border-border/50">
          <div className="w-8 h-8 rounded-lg overflow-hidden bg-muted flex-shrink-0">
            <StaticExerciseImage src={replacingExercise.gifUrl || replacingExercise.image_url} alt={replacingExercise.name} size="sm" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground leading-none">{t('exercisePicker.replacing')}</p>
            <p className="text-xs font-semibold truncate">{replacingExercise.name}</p>
            {replacingExercise.muscle_group && <p className="text-xs text-primary leading-none mt-0.5">{translateMuscle(replacingExercise.muscle_group, language)}</p>}
          </div>
        </div>
      )}
      {/* Search bar */}
      <div className="px-4 pt-1 pb-2 space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchRef}
            placeholder={t('workout.adhoc.searchExercise') || 'Search exercises...'}
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10 pr-10 h-11 bg-muted/40 border-border focus:border-primary/50 text-sm rounded-xl"
            data-testid="exercise-picker-search"
          />
          {search && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => { setSearch(''); fetchExercises('', muscleFilter, equipmentFilter, bodyPartFilter); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="exercise-picker-clear-search"
                >
                  <X className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{t('tooltips.clearSearch')}</TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Filter + Sort bar */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all
              ${hasFilters ? 'bg-primary/10 text-primary border border-primary/30' : 'bg-muted/60 text-muted-foreground hover:text-foreground border border-border'}`}
            data-testid="exercise-picker-filter-btn"
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            {t('exercisePicker.filters')}
            {activeFilterCount > 0 && (
              <span className="ml-0.5 px-1.5 py-0 rounded-full bg-primary text-white text-[9px] font-bold">{activeFilterCount}</span>
            )}
          </button>

          <button
            onClick={() => setShowSortMenu(!showSortMenu)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-muted/60 text-muted-foreground hover:text-foreground border border-border transition-all"
            data-testid="exercise-picker-sort-btn"
          >
            <ArrowUpDown className="h-3.5 w-3.5" />
            {sortBy === 'relevance' ? t('exercisePicker.sortRelevance') : sortBy === 'alpha' ? t('exercisePicker.sortAZ') : t('exercisePicker.sortMuscle')}
          </button>

          {hasFilters && (
            <button
              onClick={clearAllFilters}
              className="text-xs text-muted-foreground hover:text-primary transition-colors ml-auto"
              data-testid="exercise-picker-clear-filters"
            >
              {t('common.clearAll')}
            </button>
          )}
        </div>

        {/* Sort dropdown */}
        {showSortMenu && (
          <div className="flex gap-1.5 flex-wrap">
            {[
              { key: 'relevance', label: t('exercisePicker.sortRelevance') },
              { key: 'alpha', label: t('exercisePicker.sortAZ') },
              { key: 'muscle', label: t('exercisePicker.sortByMuscle') },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => { setSortBy(opt.key); setShowSortMenu(false); }}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all
                  ${sortBy === opt.key ? 'bg-primary/10 text-primary border border-primary/30' : 'bg-muted/60 text-muted-foreground hover:text-foreground border border-border'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* Filter chips */}
        {showFilters && (
          <div className="space-y-2 pb-1">
            {/* Body Part */}
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground/60 mb-1.5 font-semibold">{t('exercisePicker.bodyPart')}</p>
              <div className="flex flex-wrap gap-1">
                {BODY_PARTS.map(bp => (
                  <button
                    key={bp}
                    onClick={() => applyFilter('bodyPart', bp)}
                    className={`px-2 py-1 rounded-lg text-xs font-medium transition-all
                      ${bodyPartFilter === bp ? 'bg-primary/10 text-primary border border-primary/30' : 'bg-muted/60 text-muted-foreground hover:text-foreground border border-transparent hover:border-border'}`}
                    data-testid={`filter-bp-${bp}`}
                  >
                    {translateBodyPart(bp, language)}
                  </button>
                ))}
              </div>
            </div>
            {/* Target Muscle */}
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground/60 mb-1.5 font-semibold">{t('exercisePicker.targetMuscle')}</p>
              <div className="flex flex-wrap gap-1">
                {TARGET_MUSCLES.map(tm => (
                  <button
                    key={tm}
                    onClick={() => applyFilter('muscle', tm)}
                    className={`px-2 py-1 rounded-lg text-xs font-medium transition-all
                      ${muscleFilter === tm ? 'bg-primary/10 text-primary border border-primary/30' : 'bg-muted/60 text-muted-foreground hover:text-foreground border border-transparent hover:border-border'}`}
                    data-testid={`filter-tm-${tm}`}
                  >
                    {translateMuscle(tm, language)}
                  </button>
                ))}
              </div>
            </div>
            {/* Equipment */}
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground/60 mb-1.5 font-semibold">{t('exercisePicker.equipment')}</p>
              <div className="flex flex-wrap gap-1">
                {EQUIPMENT_LIST.map(eq => (
                  <button
                    key={eq}
                    onClick={() => applyFilter('equipment', eq)}
                    className={`px-2 py-1 rounded-lg text-xs font-medium transition-all
                      ${equipmentFilter === eq ? 'bg-primary/10 text-primary border border-primary/30' : 'bg-muted/60 text-muted-foreground hover:text-foreground border border-transparent hover:border-border'}`}
                    data-testid={`filter-eq-${eq}`}
                  >
                    {translateEquipment(eq, language)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Active filter tags */}
        {hasFilters && !showFilters && (
          <div className="flex flex-wrap gap-1.5">
            {muscleFilter && (
              <Badge variant="outline" className="text-xs gap-1 px-2 py-0.5 bg-orange-500/10 text-orange-400 border-orange-500/20 cursor-pointer hover:bg-orange-500/20"
                onClick={() => applyFilter('muscle', muscleFilter)} data-testid="active-filter-muscle">
                <Target className="h-2.5 w-2.5" />{translateMuscle(muscleFilter, language)}<X className="h-2.5 w-2.5 ml-0.5" />
              </Badge>
            )}
            {equipmentFilter && (
              <Badge variant="outline" className="text-xs gap-1 px-2 py-0.5 bg-blue-500/10 text-blue-400 border-blue-500/20 cursor-pointer hover:bg-blue-500/20"
                onClick={() => applyFilter('equipment', equipmentFilter)} data-testid="active-filter-equipment">
                <Dumbbell className="h-2.5 w-2.5" />{translateEquipment(equipmentFilter, language)}<X className="h-2.5 w-2.5 ml-0.5" />
              </Badge>
            )}
            {bodyPartFilter && (
              <Badge variant="outline" className="text-xs gap-1 px-2 py-0.5 bg-emerald-500/10 text-emerald-400 border-emerald-500/20 cursor-pointer hover:bg-emerald-500/20"
                onClick={() => applyFilter('bodyPart', bodyPartFilter)} data-testid="active-filter-bodypart">
                <Zap className="h-2.5 w-2.5" />{translateBodyPart(bodyPartFilter, language)}<X className="h-2.5 w-2.5 ml-0.5" />
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Scrollable results */}
      <div className="flex-1 overflow-y-auto min-h-0 px-1" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
        {/* Tabs — disabled visually when searching/filtering */}
        <div className="flex gap-1 px-4 pb-2 pt-1">
          {replacingExercise && (
            <button
              onClick={() => { if (!search && !hasFilters) setActiveTab('similar'); }}
              disabled={!!(search || hasFilters)}
              className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all ${search || hasFilters ? 'bg-muted/30 text-muted-foreground/40 cursor-not-allowed' : activeTab === 'similar' ? 'bg-primary text-white' : 'bg-muted/60 text-muted-foreground hover:text-foreground'}`}
            >
              {t('exercisePicker.similar')} {similarExercises.length > 0 && `(${similarExercises.length})`}
            </button>
          )}
          <button
            onClick={() => { if (!search && !hasFilters) setActiveTab('recent'); }}
            disabled={!!(search || hasFilters)}
            className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all ${search || hasFilters ? 'bg-muted/30 text-muted-foreground/40 cursor-not-allowed' : activeTab === 'recent' ? 'bg-primary text-white' : 'bg-muted/60 text-muted-foreground hover:text-foreground'}`}
          >
            {t('exercisePicker.recent')} {recentExercises.length > 0 && `(${recentExercises.length})`}
          </button>
          <button
            onClick={() => { if (!search && !hasFilters) setActiveTab('all'); }}
            disabled={!!(search || hasFilters)}
            className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all ${search || hasFilters ? 'bg-muted/30 text-muted-foreground/40 cursor-not-allowed' : activeTab === 'all' ? 'bg-primary text-white' : 'bg-muted/60 text-muted-foreground hover:text-foreground'}`}
          >
            {t('exercisePicker.all')}
          </button>
        </div>

        {/* Tab content */}
        {!search && !hasFilters && activeTab === 'similar' && replacingExercise && (
          <>
            {similarExercises.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">{t('exercisePicker.noSimilar')}</div>
            ) : (
              similarExercises.map(ex => (
                <ExerciseRow key={`similar-${ex.id || ex.name}`} exercise={ex} onSelect={handleSelect} onDetail={handleDetail} useCount={0} />
              ))
            )}
          </>
        )}

        {!search && !hasFilters && activeTab === 'recent' && (
          <>
            {recentExercises.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">{t('exercisePicker.noRecent')}</div>
            ) : (
              recentExercises.map(ex => (
                <ExerciseRow key={`recent-${ex.name}`} exercise={ex} onSelect={handleSelect} onDetail={handleDetail} isRecent useCount={ex.use_count || 0} />
              ))
            )}
          </>
        )}

        {/* All tab or search/filter results */}
        {(search || hasFilters || activeTab === 'all') && (
          <>
            {loading && sortedResults.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            )}
            {sortedResults.map(ex => (
              <ExerciseRow key={ex.id || ex.name} exercise={ex} onSelect={handleSelect} onDetail={handleDetail} useCount={0} />
            ))}
          </>
        )}

        {!loading && sortedResults.length === 0 && !showCreate && (search || hasFilters || activeTab === 'all') && (
          <div className="text-center py-8 px-4" data-testid="no-results-container">
            <Dumbbell className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{t('common.noResults') || 'No exercises found'}</p>
            
            {/* "Did you mean...?" suggestions */}
            {search && (suggestions.length > 0 || correctedQuery) && (
              <div className="mt-4 text-left max-w-xs mx-auto" data-testid="did-you-mean">
                <p className="text-xs font-semibold text-muted-foreground mb-2">
                  {correctedQuery ? t('exercisePicker.didYouMeanQuery', { query: correctedQuery }) : t('exercisePicker.didYouMean')}
                </p>
                <div className="space-y-1">
                  {correctedQuery && (
                    <button
                      onClick={() => { setSearch(correctedQuery); handleSearchChange(correctedQuery); }}
                      className="w-full text-left px-3 py-2 rounded-lg bg-primary/10 hover:bg-primary/90 border border-primary/30 text-primary text-xs font-medium transition-all flex items-center gap-2"
                      data-testid="suggest-corrected-query"
                    >
                      <Search className="h-3 w-3 flex-shrink-0" />
                      Search for "{correctedQuery}"
                    </button>
                  )}
                  {suggestions.map((name, i) => (
                    <button
                      key={name}
                      onClick={() => { setSearch(name); handleSearchChange(name); }}
                      className="w-full text-left px-3 py-2 rounded-lg bg-muted/60 hover:bg-muted active:bg-muted border border-border/50 hover:border-border text-foreground text-xs transition-all flex items-center gap-2"
                      data-testid={`suggest-exercise-${i}`}
                    >
                      <Dumbbell className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                      <span className="truncate">{name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {hasFilters && (
              <button onClick={clearAllFilters} className="text-xs text-primary mt-3 hover:underline">{t('exercisePicker.clearFilters')}</button>
            )}
            <button
              onClick={() => setShowCreate(true)}
              className="mt-3 px-4 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white font-bold text-xs transition-colors flex items-center gap-2 mx-auto"
              data-testid="create-exercise-from-empty"
            >
              <Plus className="h-3.5 w-3.5" />
              {t('exercisePicker.createQuoted', { name: search || t('exercisePicker.customExercise') })}
            </button>
          </div>
        )}

        {/* Permanent Create Exercise button */}
        {!loading && sortedResults.length > 0 && !showCreate && (search || hasFilters || activeTab === 'all') && (
          <div className="px-3 py-2">
            <button
              onClick={() => setShowCreate(true)}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-dashed border-border hover:border-primary/50 text-muted-foreground hover:text-primary transition-all group"
              data-testid="create-exercise-btn"
            >
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/90 transition-colors">
                <Plus className="h-5 w-5 text-primary" />
              </div>
              <div className="text-left">
                <p className="text-[13px] font-semibold">{t('exercisePicker.createCustomExercise')}</p>
                <p className="text-xs text-muted-foreground">{t('exercisePicker.createCustomDesc')}</p>
              </div>
            </button>
          </div>
        )}

        {/* Bottom padding for mobile */}
        <div className="h-4" />
      </div>
      </>
      )}
    </div>
  );

  // ─── Exercise detail modal (replaces old inline overlay) ───────
  const detailModal = (
    <ExerciseDetailModal
      exercise={exerciseDetail}
      open={!!exerciseDetail}
      onClose={() => setExerciseDetail(null)}
      onAdd={(ex) => { handleSelect(ex); setExerciseDetail(null); }}
    />
  );

  // ─── Render: Sheet on mobile, Dialog on desktop ────────────────
  if (isMobile) {
    return (<>
      <Sheet open={open} onOpenChange={(val) => { if (!val && !exerciseDetail) onClose(); }} modal={!exerciseDetail}>
        <SheetContent side="bottom" className="h-[100dvh] rounded-t-2xl p-0 flex flex-col border-border bg-background z-[310] [&>button.absolute]:hidden" overlayClassName="z-[305]">
          <SheetHeader className="px-4 pt-4 pb-0 flex-shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-base font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                {title || t('workout.adhoc.title') || 'Add Exercise'}
              </SheetTitle>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={onClose} className="min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 md:p-1 flex items-center justify-center text-muted-foreground hover:text-foreground active:bg-muted rounded" data-testid="exercise-picker-close">
                    <X className="h-5 w-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t('common.close')}</TooltipContent>
              </Tooltip>
            </div>
          </SheetHeader>
          <div className="flex-1 min-h-0 overflow-y-auto relative" style={{ WebkitOverflowScrolling: 'touch' }}>
            {pickerContent}
          </div>
        </SheetContent>
      </Sheet>
      {detailModal}
    </>);
  }

  return (<>
    <Dialog open={open} onOpenChange={(val) => { if (!val && !exerciseDetail) onClose(); }} modal={!exerciseDetail}>
      <DialogContent className="max-w-lg max-h-[85dvh] min-h-[70dvh] p-0 flex flex-col overflow-hidden border-border bg-background z-[310]" overlayClassName="z-[305]">
        <DialogHeader className="px-4 pt-4 pb-0 flex-shrink-0">
          <DialogTitle className="text-base font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {title || t('workout.adhoc.title') || 'Add Exercise'}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto relative" style={{ WebkitOverflowScrolling: 'touch' }}>
          {pickerContent}
        </div>
      </DialogContent>
    </Dialog>
    {detailModal}
  </>);
}
